/**
 * Single client for the /api/agent/chat Pages Function -> APISIX gateway
 * -> LightRAG / nemotron-nvfp4 in the foyrmind cluster.
 *
 * Used by both AgentsChat (Tracker / Design contextual chat) and
 * DesignerChat (Landing copilot) so the chat is graph-powered EVERYWHERE.
 * No keyword stubs. CLAUDE.md §3 (one gateway) + §5 (no silent catches)
 * + memory feedback_react_lightrag_agents.md.
 *
 * On HTTP 503 (AGENT_BASE_URL unbound) we surface a structured "agent brain
 * not bound" payload so the cockpit can render an honest unbound message.
 */

export interface TimelineSnapshotItem {
  id: string;
  verb: string;
  actorKind: string;
  actorId: string;
  summary: string;
  atTime: string;
  citation?: string;
}

export interface ChatHistoryTurn {
  role: "user" | "agent";
  text: string;
  actorId?: string;
  ts?: string;
}

export interface AgentChatRequest {
  projectId: string;
  question: string;
  history: ReadonlyArray<ChatHistoryTurn>;
  defaultAgentId: string | undefined;
  /** Compact slice of typed :TimelineEvent nodes the brain feeds into
   *  its LightRAG retrieval (Tantivy + bge-m3) so chat history loops
   *  back into the graph context. */
  timelineSnapshot: ReadonlyArray<TimelineSnapshotItem>;
}

export type AgentChatResult =
  | { ok: true; text: string; cited: ReadonlyArray<string>; actorId: string }
  | { ok: false; status: number; detail: string };

export async function askAgent(args: AgentChatRequest): Promise<AgentChatResult> {
  try {
    const r = await fetch("/api/agent/chat", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(args),
    });
    const text = await r.text();
    if (!r.ok) {
      try {
        const body = JSON.parse(text) as {
          detail?: string;
          status?: number;
          expected?: { model: string; route: string; retrieval: string; memory: string };
        };
        const detail = body.detail ?? `HTTP ${r.status}`;
        const expected = body.expected
          ? `\n\nWhat the brain WOULD do:\n• Model: ${body.expected.model}\n• Route: ${body.expected.route}\n• Retrieval: ${body.expected.retrieval}\n• Memory: ${body.expected.memory}`
          : "";
        return { ok: false, status: r.status, detail: detail + expected };
      } catch {
        return { ok: false, status: r.status, detail: text.slice(0, 200) };
      }
    }
    const body = JSON.parse(text) as { text?: string; cited?: string[]; actorId?: string };
    return {
      ok: true,
      text: body.text ?? "(empty reply)",
      cited: body.cited ?? [],
      actorId: body.actorId ?? args.defaultAgentId ?? "supervisor",
    };
  } catch (err) {
    return { ok: false, status: 0, detail: `Network error: ${err instanceof Error ? err.message : "unknown"}` };
  }
}
